import {
  isProviderInstancePickerReady,
  NO_PROVIDER_MODEL_SELECTION,
  type ProviderInstanceEntry,
  resolveProviderDriverKindForInstanceSelection,
} from "../providerInstances";
import {
  type EnvironmentId,
  isProviderDriverKind,
  ProjectId,
  type ModelSelection,
  ProviderDriverKind,
  type ProviderInstanceId,
  type ProviderInteractionMode,
  type ServerProvider,
  type ServerProviderSkill,
  type ChatAttachment,
  type MessageId,
  type ScopedThreadRef,
  type ThreadId,
  type TurnId,
} from "@afar-research/contracts";
import type { EnvironmentConnectionPresentation } from "@afar-research/client-runtime/state/presentation";
import { type ChatMessage, type SessionPhase, type Thread, type ThreadShell } from "../types";
import { type ComposerImageAttachment, type DraftThreadState } from "../composerDraftStore";
import * as Schema from "effect/Schema";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { environmentThreadDetails } from "../state/threads";
import {
  filterTerminalContextsWithText,
  stripInlineTerminalContextPlaceholders,
  type TerminalContextDraft,
} from "../lib/terminalContext";
import type { DraftThreadEnvMode } from "../composerDraftStore";
import { createOwnedObjectUrl, releaseOwnedObjectUrl } from "../workSurfaceResources";
import { parseStandaloneComposerSlashCommand } from "../composer-logic";
import { isThreadWorking } from "../session-logic";

/** An Environment named by a route, carrying the connection state a surface reports. */
export type EnvironmentUnavailableState = {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly connection: EnvironmentConnectionPresentation;
};

export const LAST_INVOKED_SCRIPT_BY_PROJECT_KEY = "afar-research:last-invoked-script-by-project";
export const MAX_HIDDEN_MOUNTED_TERMINAL_THREADS = 10;
export const MAX_HIDDEN_MOUNTED_PREVIEW_THREADS = 3;

export const LastInvokedScriptByProjectSchema = Schema.Record(ProjectId, Schema.String);

export function resolveTimelineProviderSkills(input: {
  readonly statusSkills: ReadonlyArray<ServerProviderSkill>;
  readonly discoveredSkills: ReadonlyArray<ServerProviderSkill>;
  readonly discoveryReady: boolean;
}): ReadonlyArray<ServerProviderSkill> {
  return input.discoveryReady ? input.discoveredSkills : input.statusSkills;
}

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
  fallbackModelSelection: ModelSelection,
): Thread {
  return {
    id: threadId,
    environmentId: draftThread.environmentId,
    projectId: draftThread.projectId,
    title: "New thread",
    modelSelection: fallbackModelSelection,
    runtimeMode: draftThread.runtimeMode,
    interactionMode: draftThread.interactionMode,
    session: null,
    messages: [],
    createdAt: draftThread.createdAt,
    updatedAt: draftThread.createdAt,
    archivedAt: null,
    deletedAt: null,
    latestTurn: null,
    branch: draftThread.branch,
    worktreePath: draftThread.worktreePath,
    checkpoints: [],
    activities: [],
    proposedPlans: [],
  };
}

export function shouldWriteThreadErrorToCurrentServerThread(input: {
  serverThread:
    | {
        environmentId: EnvironmentId;
        id: ThreadId;
      }
    | null
    | undefined;
  routeThreadRef: ScopedThreadRef;
  targetThreadId: ThreadId;
}): boolean {
  return Boolean(
    input.serverThread &&
    input.targetThreadId === input.routeThreadRef.threadId &&
    input.serverThread.environmentId === input.routeThreadRef.environmentId &&
    input.serverThread.id === input.targetThreadId,
  );
}

export function buildThreadTurnInterruptInput(
  thread: Pick<Thread, "id" | "session" | "updatedAt">,
): {
  threadId: ThreadId;
  turnId?: TurnId;
  expectedRevision: string;
} {
  const runningTurnId = thread.session?.status === "running" ? thread.session.activeTurnId : null;
  return {
    threadId: thread.id,
    expectedRevision: thread.updatedAt,
    ...(runningTurnId !== null ? { turnId: runningTurnId } : {}),
  };
}

/**
 * What a stop press is aimed at, so the composer can show the press landed
 * until that exact work is gone: the running turn, or the session still
 * starting. Any other state has nothing to stop and yields null.
 *
 * The provider may take seconds to act on an interrupt (Codex finishes booting
 * the turn's MCP servers first), and a stop button that looks untouched for
 * those seconds is what gets pressed again.
 */
export function resolveStopRequestKey(
  thread: Pick<Thread, "session"> | null | undefined,
): string | null {
  const session = thread?.session;
  if (!session) return null;
  if (session.status === "running" && session.activeTurnId !== null) {
    return `turn:${session.activeTurnId}`;
  }
  if (session.status === "starting") {
    return `session:${session.updatedAt}`;
  }
  return null;
}

export function reconcileMountedTerminalThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadTerminalOpen: boolean;
  maxHiddenThreadCount?: number;
}): string[] {
  return reconcileRetainedMountedThreadIds({
    currentThreadIds: input.currentThreadIds,
    openThreadIds: input.openThreadIds,
    activeThreadId: input.activeThreadId,
    activeThreadOpen: input.activeThreadTerminalOpen,
    maxHiddenThreadCount: input.maxHiddenThreadCount ?? MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
  });
}

export function reconcileRetainedMountedThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadOpen: boolean;
  maxHiddenThreadCount: number;
  retainInactiveActiveThread?: boolean;
}): string[] {
  const openThreadIdSet = new Set(input.openThreadIds);
  const hiddenThreadIds = input.currentThreadIds.filter(
    (threadId) =>
      (threadId !== input.activeThreadId || input.retainInactiveActiveThread === true) &&
      openThreadIdSet.has(threadId),
  );
  const maxHiddenThreadCount = Math.max(0, input.maxHiddenThreadCount);
  const nextThreadIds =
    hiddenThreadIds.length > maxHiddenThreadCount
      ? hiddenThreadIds.slice(-maxHiddenThreadCount)
      : hiddenThreadIds;

  if (
    input.activeThreadId &&
    input.activeThreadOpen &&
    !nextThreadIds.includes(input.activeThreadId)
  ) {
    nextThreadIds.push(input.activeThreadId);
  }

  return nextThreadIds;
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  releaseOwnedObjectUrl(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") {
      continue;
    }
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

export function collectUserMessageBlobPreviewUrls(message: ChatMessage): string[] {
  if (message.role !== "user" || !message.attachments) {
    return [];
  }
  const previewUrls: string[] = [];
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") continue;
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrls.push(attachment.previewUrl);
  }
  return previewUrls;
}

export interface PullRequestDialogState {
  initialReference: string | null;
  key: number;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Could not read image data."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read image."));
    });
    reader.readAsDataURL(file);
  });
}

export function resolveSendEnvMode(input: {
  requestedEnvMode: DraftThreadEnvMode;
  isGitRepo: boolean;
}): DraftThreadEnvMode {
  return input.isGitRepo ? input.requestedEnvMode : "local";
}

export function cloneComposerImageForRetry(
  image: ComposerImageAttachment,
): ComposerImageAttachment {
  if (typeof URL === "undefined" || !image.previewUrl.startsWith("blob:")) {
    return image;
  }
  try {
    return {
      ...image,
      previewUrl: createOwnedObjectUrl(image.file, {
        ownerIdentity: `composer-retry:${image.id}`,
        resourceIdentity: "image-preview",
        lifetime: "retained-owner",
      }),
    };
  } catch {
    return image;
  }
}

export function deriveComposerSendState(options: {
  prompt: string;
  imageCount: number;
  terminalContexts: ReadonlyArray<TerminalContextDraft>;
  /**
   * Optional element-pick attachment count. Element contexts contribute to
   * "sendable content" exactly like images and (text-bearing) terminal
   * contexts do: a prompt of just element chips is still a valid send.
   */
  elementContextCount?: number;
  designSystemMentionCount?: number;
}): {
  trimmedPrompt: string;
  sendableTerminalContexts: TerminalContextDraft[];
  expiredTerminalContextCount: number;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = stripInlineTerminalContextPlaceholders(options.prompt).trim();
  const sendableTerminalContexts = filterTerminalContextsWithText(options.terminalContexts);
  const expiredTerminalContextCount =
    options.terminalContexts.length - sendableTerminalContexts.length;
  const elementContextCount = options.elementContextCount ?? 0;
  const designSystemMentionCount = options.designSystemMentionCount ?? 0;
  return {
    trimmedPrompt,
    sendableTerminalContexts,
    expiredTerminalContextCount,
    hasSendableContent:
      trimmedPrompt.length > 0 ||
      options.imageCount > 0 ||
      sendableTerminalContexts.length > 0 ||
      elementContextCount > 0 ||
      designSystemMentionCount > 0,
  };
}

export function resolveAttachmentOnlyPrompt(input: {
  readonly imageCount: number;
  readonly fileCount: number;
  readonly folderCount: number;
}): string {
  const attachmentKinds = [
    ...(input.imageCount > 0 ? ["images"] : []),
    ...(input.fileCount > 0 ? ["files"] : []),
    ...(input.folderCount > 0 ? ["folders"] : []),
  ];
  const contextKinds = [
    ...(input.imageCount > 0 ? ["image(s)"] : []),
    ...(input.fileCount > 0 ? ["file snapshot(s)"] : []),
    ...(input.folderCount > 0 ? ["folder snapshot(s)"] : []),
  ];
  const describe = (values: ReadonlyArray<string>, fallback: string) =>
    values.length === 0
      ? fallback
      : values.length === 1
        ? values[0]
        : `${values.slice(0, -1).join(", ")} and ${values.at(-1)}`;

  return `[User attached one or more ${describe(attachmentKinds, "attachments")} without additional text. Respond using the conversation context and the attached ${describe(contextKinds, "snapshot(s)")}.]`;
}

export function buildExpiredTerminalContextToastCopy(
  expiredTerminalContextCount: number,
  variant: "omitted" | "empty",
): { title: string; description: string } {
  const count = Math.max(1, Math.floor(expiredTerminalContextCount));
  const noun = count === 1 ? "Expired terminal context" : "Expired terminal contexts";
  if (variant === "empty") {
    return {
      title: `${noun} won't be sent`,
      description: "Remove it or re-add it to include terminal output.",
    };
  }
  return {
    title: `${noun} omitted from message`,
    description: "Re-add it if you want that terminal output included.",
  };
}

export function threadHasStarted(thread: Thread | null | undefined): boolean {
  return Boolean(
    thread && (thread.latestTurn !== null || thread.messages.length > 0 || thread.session !== null),
  );
}

/**
 * The shell answer to {@link threadHasStarted}, for the routed thread before
 * its detail loads. A shell carries no messages, so the timestamp of the last
 * user message stands in for them.
 */
export function threadShellHasStarted(
  shell: Pick<ThreadShell, "latestTurn" | "latestUserMessageAt" | "session"> | null | undefined,
): boolean {
  return Boolean(
    shell &&
    (shell.latestTurn !== null || shell.latestUserMessageAt !== null || shell.session !== null),
  );
}

export function canPromoteDraftThread(thread: Thread | null | undefined): boolean {
  return thread != null;
}

/**
 * The driver a started thread is locked to, so a follow-up reaches the
 * conversation it belongs to.
 *
 * The session names the driver once the provider reports in. Until then (a
 * first send's session is still `starting`) the session's, the thread's and
 * the composer's provider instances are resolved to their driver through the
 * configured instances. An instance id is never a driver kind: a Cloud-managed
 * `codex_managed_*` or user-named instance runs the `codex` driver. An
 * instance that is no longer configured leaves the thread unlocked; routing,
 * not the lock, reports a provider that is gone.
 */
export function deriveLockedProvider(input: {
  thread: Thread | null | undefined;
  selectedProvider: ProviderInstanceId | null;
  threadProvider: ProviderInstanceId | null;
  providers: ReadonlyArray<Pick<ServerProvider, "instanceId" | "driver">>;
}): ProviderDriverKind | null {
  if (!threadHasStarted(input.thread)) {
    return null;
  }
  const sessionProvider = input.thread?.session?.providerName ?? null;
  if (sessionProvider && isProviderDriverKind(sessionProvider)) {
    return sessionProvider;
  }
  const driverOf = (instanceId: ProviderInstanceId | null | undefined) =>
    instanceId
      ? resolveProviderDriverKindForInstanceSelection(input.providers, instanceId)
      : undefined;
  return (
    driverOf(input.thread?.session?.providerInstanceId) ??
    driverOf(input.threadProvider) ??
    driverOf(input.selectedProvider) ??
    null
  );
}

export function getStartedThreadModelChangeBlockReason(input: {
  providers: ReadonlyArray<Pick<ServerProvider, "instanceId" | "requiresNewThreadForModelChange">>;
  hasStartedSession: boolean;
  currentModelSelection: ModelSelection;
  currentProviderInstanceId?: ModelSelection["instanceId"] | null | undefined;
  nextModelSelection: ModelSelection;
}): { title: string; description: string } | null {
  if (!input.hasStartedSession) {
    return null;
  }
  const currentModelSelection = {
    ...input.currentModelSelection,
    instanceId: input.currentProviderInstanceId ?? input.currentModelSelection.instanceId,
  };
  if (
    currentModelSelection.instanceId === input.nextModelSelection.instanceId &&
    currentModelSelection.model === input.nextModelSelection.model
  ) {
    return null;
  }
  const currentProvider = input.providers.find(
    (snapshot) => snapshot.instanceId === currentModelSelection.instanceId,
  );
  const nextProvider = input.providers.find(
    (snapshot) => snapshot.instanceId === input.nextModelSelection.instanceId,
  );
  if (
    currentProvider?.requiresNewThreadForModelChange !== true &&
    nextProvider?.requiresNewThreadForModelChange !== true
  ) {
    return null;
  }
  return {
    title: "Start a new chat to change models",
    description: "This provider does not allow switching models after a conversation has started.",
  };
}

export type ProposedPlanImplementationOutcome = "accepted" | "rejected" | "pending";

export async function observeProposedPlanImplementation(input: {
  turnStartCommandAccepted: boolean;
  initialOutcome: ProposedPlanImplementationOutcome;
  waitForOutcome: () => Promise<ProposedPlanImplementationOutcome>;
  onAccepted: () => void | Promise<void>;
  onRejected: () => void | Promise<void>;
}): Promise<ProposedPlanImplementationOutcome> {
  if (!input.turnStartCommandAccepted || input.initialOutcome !== "pending") {
    return input.initialOutcome;
  }
  const outcome = await input.waitForOutcome();
  if (outcome === "accepted") {
    await input.onAccepted();
  } else if (outcome === "rejected") {
    await input.onRejected();
  }
  return outcome;
}

export function resolveProposedPlanImplementationOutcome(input: {
  sourceThread: Thread | null | undefined;
  sourcePlanId: string;
  targetThread: Thread | null | undefined;
  targetThreadId: ThreadId;
  targetFailureSessionUpdatedAtBaseline?: string | null;
}): ProposedPlanImplementationOutcome {
  const sourcePlan = input.sourceThread?.proposedPlans.find(
    (plan) => plan.id === input.sourcePlanId,
  );
  if (
    sourcePlan !== undefined &&
    sourcePlan.implementedAt !== null &&
    sourcePlan.implementationThreadId === input.targetThreadId
  ) {
    return "accepted";
  }

  const targetTurn = input.targetThread?.latestTurn;
  const targetTurnReferencesSourcePlan =
    targetTurn?.sourceProposedPlan?.threadId === input.sourceThread?.id &&
    targetTurn?.sourceProposedPlan?.planId === input.sourcePlanId;
  const targetSession = input.targetThread?.session;
  const targetSessionFailureIsNew =
    targetSession?.activeTurnId === null &&
    targetSession.lastError !== null &&
    (input.targetFailureSessionUpdatedAtBaseline === undefined ||
      targetSession.updatedAt !== input.targetFailureSessionUpdatedAtBaseline);
  if (
    targetSession?.status === "error" ||
    targetSessionFailureIsNew ||
    (targetTurnReferencesSourcePlan && targetTurn?.state === "error")
  ) {
    return "rejected";
  }
  return "pending";
}

export async function waitForProposedPlanImplementationOutcome(input: {
  sourceThreadRef: ScopedThreadRef;
  sourcePlanId: string;
  targetThreadRef: ScopedThreadRef;
  targetFailureSessionUpdatedAtBaseline?: string | null;
  timeoutMs?: number;
}): Promise<ProposedPlanImplementationOutcome> {
  const sourceThreadAtom = environmentThreadDetails.detailAtom(input.sourceThreadRef);
  const targetThreadAtom = environmentThreadDetails.detailAtom(input.targetThreadRef);
  const readOutcome = () =>
    resolveProposedPlanImplementationOutcome({
      sourceThread: appAtomRegistry.get(sourceThreadAtom),
      sourcePlanId: input.sourcePlanId,
      targetThread: appAtomRegistry.get(targetThreadAtom),
      targetThreadId: input.targetThreadRef.threadId,
      ...(input.targetFailureSessionUpdatedAtBaseline !== undefined
        ? { targetFailureSessionUpdatedAtBaseline: input.targetFailureSessionUpdatedAtBaseline }
        : {}),
    });
  const initialOutcome = readOutcome();
  if (initialOutcome !== "pending") {
    return initialOutcome;
  }

  return await new Promise<ProposedPlanImplementationOutcome>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    let unsubscribeSource: () => void = () => undefined;
    let unsubscribeTarget: () => void = () => undefined;
    const finish = (outcome: ProposedPlanImplementationOutcome) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      unsubscribeSource();
      unsubscribeTarget();
      resolve(outcome);
    };
    const observe = () => {
      const outcome = readOutcome();
      if (outcome !== "pending") {
        finish(outcome);
      }
    };

    unsubscribeSource = appAtomRegistry.subscribe(sourceThreadAtom, observe);
    unsubscribeTarget = appAtomRegistry.subscribe(targetThreadAtom, observe);
    observe();
    if (input.timeoutMs !== undefined) {
      timeoutId = globalThis.setTimeout(() => finish("pending"), input.timeoutMs);
    }
  });
}

export async function waitForStartedServerThread(
  threadRef: ScopedThreadRef,
  timeoutMs = 1_000,
): Promise<boolean> {
  const threadAtom = environmentThreadDetails.detailAtom(threadRef);
  const getThread = () => appAtomRegistry.get(threadAtom);
  const thread = getThread();

  if (threadHasStarted(thread)) {
    return true;
  }

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    const finish = (result: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      unsubscribe();
      resolve(result);
    };

    const unsubscribe = appAtomRegistry.subscribe(threadAtom, (thread) => {
      if (!threadHasStarted(thread)) {
        return;
      }
      finish(true);
    });

    if (threadHasStarted(getThread())) {
      finish(true);
      return;
    }

    timeoutId = globalThis.setTimeout(() => {
      finish(false);
    }, timeoutMs);
  });
}

export async function navigateToForkedThread(input: {
  threadRef: ScopedThreadRef;
  waitForThread: (threadRef: ScopedThreadRef) => Promise<boolean>;
  navigate: (threadRef: ScopedThreadRef) => Promise<void>;
}): Promise<void> {
  await input.waitForThread(input.threadRef);
  await input.navigate(input.threadRef);
}

export interface LocalDispatchSnapshot {
  startedAt: string;
  preparingWorktree: boolean;
  latestTurnTurnId: TurnId | null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionStatus: NonNullable<Thread["session"]>["status"] | null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | undefined,
  options?: { preparingWorktree?: boolean },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  return {
    startedAt: new Date().toISOString(),
    preparingWorktree: Boolean(options?.preparingWorktree),
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionStatus: session?.status ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

/**
 * Whether the thread has a turn in progress, as the Environment records it:
 * the chat view's working indicator and the composer's stop control both read
 * it. It does not lean on the chat view's local send state, which does not
 * survive the remount when a draft becomes its server thread.
 *
 * A `connecting` session alone is not enough. Focusing the composer warms the
 * session ahead of a prompt, which also starts it; only a user message the
 * latest turn has not answered makes that start this thread's own send.
 */
export function isTurnInProgress(input: {
  phase: SessionPhase;
  latestTurn: Pick<NonNullable<Thread["latestTurn"]>, "requestedAt"> | null;
  latestUserMessageAt: string | null;
}): boolean {
  if (!isThreadWorking(input.phase)) return false;
  if (input.phase === "running") return true;
  return (
    input.latestUserMessageAt !== null &&
    (input.latestTurn === null || input.latestUserMessageAt > input.latestTurn.requestedAt)
  );
}

export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  session: Thread["session"] | null;
  hasPendingApproval: boolean;
  hasPendingUserInput: boolean;
  threadError: string | null | undefined;
}): boolean {
  if (!input.localDispatch) {
    return false;
  }
  if (input.hasPendingApproval || input.hasPendingUserInput || Boolean(input.threadError)) {
    return true;
  }
  // A session that is still starting has not acknowledged anything yet: the
  // Environment is launching the provider, which is the slowest stretch of a
  // send. `connecting` is not a phase the working state may be handed back in,
  // or the thread reads as idle for the whole launch.
  if (input.phase === "connecting") {
    return false;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const latestTurnChanged =
    input.localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    input.localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    input.localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    input.localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  const dispatchedDuringActiveTurn =
    input.localDispatch.latestTurnStartedAt !== null &&
    input.localDispatch.latestTurnCompletedAt === null;

  if (input.phase === "running") {
    if (dispatchedDuringActiveTurn) {
      // FIFO enqueue / follow-up while a turn is already running: the server
      // acknowledges by accepting the request (often a not-yet-started latest
      // turn). Waiting for that turn to become the active started turn leaves
      // isSendBusy stuck on "Queueing..." and blocks further queues.
      return (
        latestTurnChanged ||
        input.localDispatch.sessionStatus !== (session?.status ?? null) ||
        input.localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
      );
    }
    if (!latestTurnChanged) {
      return false;
    }
    if (latestTurn?.startedAt === null || latestTurn === null) {
      return false;
    }
    if (
      session?.activeTurnId !== null &&
      session?.activeTurnId !== undefined &&
      latestTurn?.turnId !== session.activeTurnId
    ) {
      return false;
    }
    return true;
  }

  return (
    latestTurnChanged ||
    input.localDispatch.sessionStatus !== (session?.status ?? null) ||
    input.localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
  );
}

/** A running turn already owns the provider; further sends enqueue onto the Thread Queue. */
export function shouldBlockOutgoingSend(input: {
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly sendInFlight: boolean;
  readonly environmentUnavailable: boolean;
  readonly phase: SessionPhase;
}): boolean {
  if (input.isConnecting || input.environmentUnavailable) {
    return true;
  }
  if (input.phase === "running") {
    return false;
  }
  return input.isSendBusy || input.sendInFlight;
}

export function resolveOutgoingSendComposerSource<T>(input: {
  readonly overridePrompt?: string;
  readonly overridePathAttachments?: ReadonlyArray<T>;
  readonly composerPrompt: string;
  readonly composerPathAttachments: ReadonlyArray<T>;
}): {
  readonly prompt: string;
  readonly pathAttachments: ReadonlyArray<T>;
  readonly consumeComposerDraft: boolean;
} {
  const hasOverridePrompt = input.overridePrompt !== undefined;
  const hasOverridePathAttachments = input.overridePathAttachments !== undefined;
  return {
    prompt: input.overridePrompt ?? input.composerPrompt,
    pathAttachments: input.overridePathAttachments ?? input.composerPathAttachments,
    consumeComposerDraft: !hasOverridePrompt && !hasOverridePathAttachments,
  };
}

/**
 * What one composer send should actually do.
 *
 * A send is not always a turn: it may advance a pending user input, submit a
 * plan follow-up, be a bare interaction-mode slash command, or carry nothing
 * sendable at all. Each of those used to be an early return buried in a
 * 600-line handler, so the order between them — which one wins when several
 * apply — could only be recovered by reading the whole handler.
 */
export type OutgoingSendDisposition =
  /** Nothing to do: no thread, a blocked send, no composer context, or no project. */
  | { readonly type: "ignore" }
  | { readonly type: "advance-pending-user-input" }
  | { readonly type: "submit-plan-follow-up" }
  | { readonly type: "apply-interaction-mode"; readonly mode: "plan" | "default" }
  | { readonly type: "nothing-to-send"; readonly expiredTerminalContextCount: number }
  | { readonly type: "send" };

/** The composer's contribution, or null when it has no send context to give. */
export interface OutgoingSendComposerState {
  readonly trimmedPrompt: string;
  readonly hasSendableContent: boolean;
  readonly expiredTerminalContextCount: number;
  /** False when the caller supplied an override prompt, so the draft is untouched. */
  readonly consumeComposerDraft: boolean;
  /**
   * Images, path attachments, sendable terminal contexts, element contexts,
   * preview annotations, review comments, design system mentions and
   * registered machine mentions. A bare slash command carries none of them.
   */
  readonly attachedContentCount: number;
}

export function resolveOutgoingSendDisposition(input: {
  readonly hasActiveThread: boolean;
  readonly blocked: boolean;
  readonly hasActivePendingUserInput: boolean;
  readonly composer: OutgoingSendComposerState | null;
  readonly showPlanFollowUpPrompt: boolean;
  readonly hasActiveProposedPlan: boolean;
  readonly hasActiveProject: boolean;
}): OutgoingSendDisposition {
  if (!input.hasActiveThread || input.blocked) {
    return { type: "ignore" };
  }
  // An unanswered prompt owns the composer: Enter advances it rather than
  // starting a turn beside it.
  if (input.hasActivePendingUserInput) {
    return { type: "advance-pending-user-input" };
  }
  const composer = input.composer;
  if (composer === null) {
    return { type: "ignore" };
  }
  if (
    composer.consumeComposerDraft &&
    input.showPlanFollowUpPrompt &&
    input.hasActiveProposedPlan
  ) {
    return { type: "submit-plan-follow-up" };
  }
  const standaloneSlashCommand =
    composer.consumeComposerDraft && composer.attachedContentCount === 0
      ? parseStandaloneComposerSlashCommand(composer.trimmedPrompt)
      : null;
  if (standaloneSlashCommand) {
    return { type: "apply-interaction-mode", mode: standaloneSlashCommand };
  }
  if (!composer.hasSendableContent) {
    return {
      type: "nothing-to-send",
      expiredTerminalContextCount: composer.expiredTerminalContextCount,
    };
  }
  if (!input.hasActiveProject) {
    return { type: "ignore" };
  }
  return { type: "send" };
}

/**
 * The Environment a thread route depends on, when it is not reachable.
 *
 * A thread route that cannot draw its thread has two very different reasons to be empty:
 * nothing is selected, or the Environment holding it is unreachable. Only the second has
 * anything to tell the user, and a draft route never has it — a draft belongs to the
 * client until it is sent, so an unreachable Environment is not why it has no thread.
 */
export function resolveRouteEnvironmentUnavailability(input: {
  readonly routeKind: "server" | "draft";
  readonly environment: EnvironmentUnavailableState | null;
}): EnvironmentUnavailableState | null {
  if (input.routeKind !== "server" || input.environment === null) {
    return null;
  }
  return input.environment.connection.phase === "ready" ? null : input.environment;
}

/**
 * Which provider instance the composer targets, given what the thread, project
 * and draft each asked for. A thread locked to a driver stays on it, and on the
 * same continuation group, so a follow-up reaches the conversation it belongs
 * to rather than an unrelated account's.
 */
export function resolveComposerProviderSelection(input: {
  entries: ReadonlyArray<ProviderInstanceEntry>;
  candidateInstanceIds: ReadonlyArray<ProviderInstanceId | null | undefined>;
  lockedProvider: ProviderDriverKind | null;
  lockedInstanceId: ProviderInstanceId | null | undefined;
}) {
  const requestedInstanceId = input.candidateInstanceIds.find(
    (candidate) => candidate != null && candidate !== NO_PROVIDER_MODEL_SELECTION.instanceId,
  );
  const requestedDriverKind =
    input.lockedProvider ??
    input.entries.find((entry) => entry.instanceId === requestedInstanceId)?.driverKind ??
    input.entries[0]?.driverKind ??
    ProviderDriverKind.make("unconfigured");
  const lockedContinuationGroupKey = input.lockedProvider
    ? (input.entries.find((entry) => entry.instanceId === input.lockedInstanceId)
        ?.continuationGroupKey ?? null)
    : null;
  const compatibleEntries = input.entries.filter(
    (entry) =>
      (!input.lockedProvider || entry.driverKind === input.lockedProvider) &&
      (!lockedContinuationGroupKey || entry.continuationGroupKey === lockedContinuationGroupKey),
  );
  const firstSelectable = (entries: ReadonlyArray<ProviderInstanceEntry>) =>
    entries.find((entry) => isProviderInstancePickerReady(entry));
  const selectedProviderEntry =
    input.candidateInstanceIds
      .map((candidate) =>
        compatibleEntries.find(
          (entry) => entry.instanceId === candidate && entry.enabled && entry.isAvailable,
        ),
      )
      .find((entry) => entry !== undefined) ??
    firstSelectable(
      compatibleEntries.filter((entry) => entry.driverKind === requestedDriverKind),
    ) ??
    firstSelectable(compatibleEntries);
  const unavailableProviderInstanceId = selectedProviderEntry
    ? undefined
    : input.lockedProvider
      ? (input.lockedInstanceId ?? requestedInstanceId)
      : requestedInstanceId;
  return {
    selectedProviderEntry,
    requestedDriverKind,
    lockedContinuationGroupKey,
    unavailableProviderInstanceId,
  };
}

/** Keep restored drafts and every plan control on the selected instance's supported mode. */
export function resolveComposerInteractionMode(input: {
  planModeEnabled: boolean;
  provider: Pick<ServerProvider, "showInteractionModeToggle"> | null | undefined;
  interactionMode: ProviderInteractionMode;
}): { enabled: boolean; interactionMode: ProviderInteractionMode } {
  const enabled =
    input.planModeEnabled &&
    input.provider != null &&
    input.provider.showInteractionModeToggle !== false;
  return {
    enabled,
    interactionMode: enabled ? input.interactionMode : "default",
  };
}

export type RevertLandingOutcome =
  | { readonly kind: "pending" }
  | { readonly kind: "landed" }
  | { readonly kind: "failed"; readonly detail: string };

/**
 * Decide whether a requested rewind has actually landed in the read model.
 *
 * The dispatch receipt only says the command was accepted. The rewind itself
 * runs in the CheckpointReactor, which reports a failure by appending a
 * `checkpoint.revert.failed` activity rather than by failing the command, so a
 * caller that trusts the receipt would restore the prompt to the composer while
 * the thread still holds every turn the user asked to discard.
 */
export function resolveRevertLandingOutcome(input: {
  readonly thread: Thread | null | undefined;
  readonly messageId: MessageId;
  readonly turnCount: number;
  readonly knownFailureActivityIds: ReadonlySet<string>;
}): RevertLandingOutcome {
  const thread = input.thread;
  if (!thread) return { kind: "pending" };

  const failure = thread.activities.findLast(
    (activity) =>
      activity.kind === "checkpoint.revert.failed" &&
      !input.knownFailureActivityIds.has(activity.id),
  );
  if (failure) {
    const payload = failure.payload;
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : failure.summary;
    return { kind: "failed", detail };
  }

  const messageGone = !thread.messages.some((message) => message.id === input.messageId);
  const checkpointsTruncated = thread.checkpoints.every(
    (checkpoint) => checkpoint.checkpointTurnCount <= input.turnCount,
  );
  const turnTruncated =
    input.turnCount === 0
      ? thread.latestTurn === null
      : thread.checkpoints.some((checkpoint) => checkpoint.turnId === thread.latestTurn?.turnId);

  return messageGone && checkpointsTruncated && turnTruncated
    ? { kind: "landed" }
    : { kind: "pending" };
}

/**
 * Resolve once the rewind is visible in the read model, or reject with the
 * reactor's own reason for refusing it.
 */
export async function waitForRevertToLand(input: {
  readonly threadRef: ScopedThreadRef;
  readonly messageId: MessageId;
  readonly turnCount: number;
  readonly timeoutMs?: number;
}): Promise<void> {
  const threadAtom = environmentThreadDetails.detailAtom(input.threadRef);
  const initial = appAtomRegistry.get(threadAtom);
  if (!initial?.messages.some((message) => message.id === input.messageId)) {
    throw new Error("The message to revert to is no longer available.");
  }
  // Only a failure appended after this point belongs to this revert.
  const knownFailureActivityIds = new Set(
    initial.activities
      .filter((activity) => activity.kind === "checkpoint.revert.failed")
      .map((activity) => activity.id),
  );

  return await new Promise<void>((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    let unsubscribe: () => void = () => undefined;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) globalThis.clearTimeout(timeoutId);
      unsubscribe();
      if (error) reject(error);
      else resolve();
    };
    const observe = () => {
      const outcome = resolveRevertLandingOutcome({
        thread: appAtomRegistry.get(threadAtom),
        messageId: input.messageId,
        turnCount: input.turnCount,
        knownFailureActivityIds,
      });
      if (outcome.kind === "landed") finish();
      else if (outcome.kind === "failed") finish(new Error(outcome.detail));
    };

    unsubscribe = appAtomRegistry.subscribe(threadAtom, observe);
    observe();
    timeoutId = globalThis.setTimeout(
      () => finish(new Error("The revert did not finish in time.")),
      input.timeoutMs ?? 120_000,
    );
  });
}

export interface RevertedImageAttachment {
  readonly attachmentId: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}

export interface RevertedAttachmentPlan {
  /** Stored bytes the revert will prune, so they must be fetched beforehand. */
  readonly images: ReadonlyArray<RevertedImageAttachment>;
  /**
   * Names of attachments the composer cannot take back. A file or folder
   * attachment is a live reference to a host path (ADR 0109), so the revert
   * never touched the user's own file — only the record of having attached it.
   */
  readonly notRestored: ReadonlyArray<string>;
}

/**
 * Split a rewound message's attachments into what the composer can carry back.
 *
 * Only images hold bytes in the attachment store, and only those bytes are
 * pruned by the revert, so only they have to be rescued before it runs.
 */
export function planRevertedMessageAttachments(
  attachments: ReadonlyArray<ChatAttachment> | undefined,
): RevertedAttachmentPlan {
  const images: Array<RevertedImageAttachment> = [];
  const notRestored: Array<string> = [];
  for (const attachment of attachments ?? []) {
    if (attachment.type === "image") {
      images.push({
        attachmentId: attachment.id,
        name: attachment.name,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
      });
      continue;
    }
    notRestored.push(attachment.name);
  }
  return { images, notRestored };
}

/**
 * Fetch a rewound message's image bytes into composer attachments.
 *
 * Called before the revert is dispatched, because afterwards the projection has
 * pruned the files these URLs point at. An image that cannot be fetched is
 * skipped rather than failing the revert: losing an attachment from the
 * composer is a far smaller harm than leaving history half-rewound.
 */
export async function fetchRevertedImageAttachments(input: {
  readonly images: ReadonlyArray<RevertedImageAttachment>;
  readonly resolveUrl: (attachmentId: string) => Promise<string | null>;
  readonly makeId: () => string;
  readonly ownerIdentity: string;
}): Promise<ReadonlyArray<ComposerImageAttachment>> {
  const restored: Array<ComposerImageAttachment> = [];
  for (const image of input.images) {
    try {
      const url = await input.resolveUrl(image.attachmentId);
      if (url === null) continue;
      const response = await globalThis.fetch(url);
      if (!response.ok) continue;
      const blob = await response.blob();
      const file = new File([blob], image.name, { type: image.mimeType });
      const id = input.makeId();
      restored.push({
        id,
        type: "image",
        name: image.name,
        mimeType: image.mimeType,
        sizeBytes: image.sizeBytes,
        previewUrl: createOwnedObjectUrl(file, {
          ownerIdentity: input.ownerIdentity,
          resourceIdentity: `reverted-attachment:${id}`,
          lifetime: "retained-owner",
        }),
        file,
      });
    } catch {
      continue;
    }
  }
  return restored;
}
